/**
 * TaskActivityGroup
 * One task's worth of activity in the drawer: a clickable header carrying the
 * task title and unread count, with its comment or note rows beneath.
 */

import { Stack, Group, Text, Badge } from '@/components/ui';
import { Avatar, Divider } from '@/components/ui/data-display';
import ActionIcon from '@/components/ui/ActionIcon';
import { IconChevronRight, IconAt, IconAlertCircle } from '@tabler/icons-react';
import type { TaskActivityGroupView, TaskActivityItemView } from '@actschurch/ccms-sdk';
import { renderMentions } from '../utils/mentionRenderer';
import { formatDate } from '../utils/taskHelpers';
import type { SeenCandidate } from '../hooks/useActivitySeenTracker';

interface TaskActivityGroupProps {
  group: TaskActivityGroupView;
  /** Rows to show — already filtered to the active tab's activity type. */
  items: TaskActivityItemView[];
  unreadCount: number;
  onOpenTask: (taskId: number) => void;
  registerItem: (candidate: SeenCandidate) => (node: HTMLElement | null) => void;
}

function initialsOf(name?: string | null) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function TaskActivityGroup({
  group,
  items,
  unreadCount,
  onOpenTask,
  registerItem,
}: TaskActivityGroupProps) {
  if (items.length === 0) return null;

  return (
    <Stack gap="xs" mb="md">
      <Group
        justify="space-between"
        wrap="nowrap"
        style={{ cursor: 'pointer' }}
        onClick={() => onOpenTask(group.taskID)}
      >
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {group.taskTitle || `Task #${group.taskID}`}
          </Text>
          {unreadCount > 0 && (
            <Badge size="xs" circle color="blue">
              {unreadCount}
            </Badge>
          )}
        </Group>
        <ActionIcon variant="subtle" size="sm" aria-label="Open task">
          <IconChevronRight size={14} />
        </ActionIcon>
      </Group>

      <Divider />

      {items.map((item) => (
        <Group
          key={`${item.activityTypeID}:${item.itemID}`}
          align="flex-start"
          gap="xs"
          wrap="nowrap"
          ref={registerItem({
            activityTypeId: item.activityTypeID,
            itemId: item.itemID,
            taskId: item.taskID,
          })}
          style={{ cursor: 'pointer', opacity: item.isSeen ? 0.65 : 1 }}
          onClick={() => onOpenTask(group.taskID)}
        >
          <Avatar src={item.createdByAvatar ?? undefined} size="sm" radius="xl">
            {initialsOf(item.createdByName)}
          </Avatar>

          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Group gap="xs" wrap="nowrap">
              <Text size="xs" fw={item.isSeen ? 400 : 600} truncate>
                {item.createdByName || 'Unknown User'}
              </Text>
              {item.isMention && (
                <span title="You were mentioned" style={{ display: 'inline-flex' }}>
                  <IconAt size={12} />
                </span>
              )}
              {item.isActionRequired && (
                <span title={item.actionLabel ?? 'Action required'} style={{ display: 'inline-flex' }}>
                  <IconAlertCircle size={12} />
                </span>
              )}
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {formatDate(item.occurredAt, 'MMM D, HH:mm')}
              </Text>
            </Group>

            {/* Bodies are authored in the rich-text composer and are already
                sanitized server-side; renderMentions swaps mention tokens for
                readable name chips so a raw GUID is never shown. */}
            <div
              className="ccms-activity-preview"
              style={{
                fontSize: 'var(--mantine-font-size-xs)',
                color: 'var(--mantine-color-dimmed)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              dangerouslySetInnerHTML={{ __html: renderMentions(item.body ?? '') }}
            />
          </Stack>
        </Group>
      ))}
    </Stack>
  );
}
